import type { AiCompanionChatRequestDto } from "@simbelapp/order-sdk";
import { DefaultChatTransport, type ChatTransport } from "ai";
import { validate as isUuid } from "uuid";
import { useCopilotResourceContext } from "~/composables/copilot/useCopilotResourceContext";
import { validCopilotResourceContext } from "~/utils/helpers/copilot/resource-context";
import { useAuthStore } from "~/store/auth/auth.store";
import { createCopilotFetch } from "~/utils/helpers/copilot/errors";
import type { ICopilotResourceContext, ICopilotUIMessage } from "~/utils/interfaces/copilot-interfaces";
import {
  assertAuthSessionCurrent,
  bindReadableStreamToSession,
  createAuthenticatedFetch,
} from "~/utils/helpers/auth/transport";

/** SSE endpoint of the assistant, on the orders API. */
const DOCUMENT_ASSISTANT_STREAM_PATH = "/ai-companion/chat";

/**
 * The API's request DTO with our typed messages (the generator flattens them to `object`). The API
 * whitelists the body keys and 400s on any extra one — here an extra key fails to compile instead.
 *
 * Require `resourceContext` on every request and keep it limited to the backend's two fields.
 */
type ICopilotChatRequestBody = Omit<AiCompanionChatRequestDto, "messages" | "resourceContext"> & {
  messages: ICopilotUIMessage[];
  resourceContext: ICopilotResourceContext | null;
  /** Session the admin has on screen, so a sheet dropped from it needs no matching pass. */
  sessionId?: string;
};

/**
 * `DefaultChatTransport` POSTs the conversation and reads back the AI SDK UI message stream
 * (`text/event-stream`): text deltas, data parts and tool calls all arrive through it.
 *
 * `chatId` is the assistant's memory thread id. The panel owns it (not the SDK chat id) so that
 * "Nouvelle discussion" can regenerate it — that is what actually resets the assistant's memory.
 *
 * `sessionId` is for callers that already know the session the sheet belongs to — the session-page
 * import does, and must not depend on the route still showing it. Everyone else falls back to
 * whichever session detail is open.
 */
export const useCopilotTransport = (
  chatId: Ref<string>,
  sessionId?: Ref<string | null | undefined>,
): ChatTransport<ICopilotUIMessage> => {
  const config = useRuntimeConfig();
  const authStore = useAuthStore();
  const pageContext = useCopilotResourceContext();
  const authenticatedFetch = createAuthenticatedFetch(authStore);

  const transport = new DefaultChatTransport<ICopilotUIMessage>({
    api: `${config.public.ORDERS_URL}${DOCUMENT_ASSISTANT_STREAM_PATH}`,
    // Preserve HTTP status and a structured `data-error` body instead of reducing both to a string.
    fetch: createCopilotFetch(authenticatedFetch),
    credentials: "omit",
    /** Runs on every send, so a long conversation never streams with an expired token. */
    prepareSendMessagesRequest: async ({ api, messages, trigger, messageId }) => {
      // Navigation can change while authentication is pending; resolve context after renewal.
      const epoch = authStore.sessionEpoch;
      await authStore.getAccessTokenForRequest(new URL(api).origin);
      assertAuthSessionCurrent(authStore, epoch);

      /*
       * Read at send time, not at setup: the panel is mounted once at the root and outlives every
       * navigation, so its context must follow the detail currently on screen.
       */
      const context = pageContext.value;
      const openSessionId = sessionId?.value ?? context.sessionId;
      const body: ICopilotChatRequestBody = {
        chatId: chatId.value,
        messages,
        trigger,
        messageId,
        resourceContext: validCopilotResourceContext(context.resourceContext),
        ...(openSessionId && isUuid(openSessionId) ? { sessionId: openSessionId } : {}),
      };

      return {
        api,
        body,
        credentials: "omit" as const,
      };
    },
  });

  return {
    async sendMessages(options) {
      const epoch = authStore.sessionEpoch;
      const stream = await transport.sendMessages(options);
      assertAuthSessionCurrent(authStore, epoch);
      return bindReadableStreamToSession(authStore, epoch, stream);
    },
    async reconnectToStream(options) {
      const epoch = authStore.sessionEpoch;
      const stream = await transport.reconnectToStream(options);
      assertAuthSessionCurrent(authStore, epoch);
      return stream ? bindReadableStreamToSession(authStore, epoch, stream) : null;
    },
  } satisfies ChatTransport<ICopilotUIMessage>;
};
